import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CurrencyDollarIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { usersApi, tokenApi } from '../services/api';
import Toast from '../components/Toast';

interface UserManagementProps {
  currentUser: {
    id: number;
    name: string;
    role: string;
  };
}

interface UserItem {
  id: number;
  username: string;
  display_name: string;
  role: string;
  is_active: boolean;
  login_method: string;
  created_at: string | null;
  last_login_at: string | null;
  project_count: number;
  token_total: number;
  token_used: number;
  token_remaining: number;
  token_remaining_pct: number;
}

// 兑换比例：1元=10万Token
const EXCHANGE_RATE = 100000;

const ROLE_LABELS: Record<string, string> = {
  super: '超级管理员',
  admin: '管理员',
  user: '普通用户',
};

const ROLE_OPTIONS = [
  { value: 'SUPER', label: '超级管理员' },
  { value: 'ADMIN', label: '管理员' },
  { value: 'USER', label: '普通用户' },
];

function formatTokenCount(n: number): string {
  if (n >= 100_000_000) return `${(n / 100_000_000).toFixed(1)}亿`;
  if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(1)}千万`;
  if (n >= 10_000) return `${(n / 10_000).toFixed(1)}万`;
  return String(n);
}

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });
  } catch { return '-'; }
}

const UserManagement: React.FC<UserManagementProps> = ({ currentUser }) => {
  const isSuper = currentUser.role === 'super';
  const isAdmin = isSuper || currentUser.role === 'admin';

  const [users, setUsers] = useState<UserItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, title: '', message: '', type: 'success' as 'success' | 'error' });

  // 角色编辑状态
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [editingRole, setEditingRole] = useState('');

  // 充值弹窗状态
  const [rechargeTarget, setRechargeTarget] = useState<UserItem | null>(null);
  const [rechargeMode, setRechargeMode] = useState<'package' | 'custom'>('package');
  const [rechargePackages, setRechargePackages] = useState<any[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [customTokens, setCustomTokens] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [rechargeLoading, setRechargeLoading] = useState(false);

  // 加载充值套餐
  const loadRechargePackages = async () => {
    try {
      const res = await tokenApi.getRechargePackages();
      const packages = res.data.data || [];
      const activePackages = packages.filter((p: any) => p.is_active);
      setRechargePackages(activePackages);
      if (activePackages.length > 0) {
        setSelectedPackage(activePackages[0]);
      }
    } catch (e) {
      console.error('加载套餐失败', e);
    }
  };

  // 打开充值弹窗
  const openRechargeDialog = async (user: UserItem) => {
    setRechargeTarget(user);
    setRechargeMode('package');
    setCustomTokens('');
    setCustomAmount('');
    await loadRechargePackages();
  };

  // 处理自定义Token输入
  const handleCustomTokensChange = (value: string) => {
    setCustomTokens(value);
    const tokens = parseInt(value) || 0;
    setCustomAmount((tokens / EXCHANGE_RATE).toFixed(2));
  };

  // 处理自定义金额输入
  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const amount = parseFloat(value) || 0;
    setCustomTokens(Math.floor(amount * EXCHANGE_RATE).toString());
  };

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const title = type === 'success' ? '操作成功' : '操作失败';
    setToast({ show: true, title, message, type });
  };

  const loadUsers = useCallback(async (kw?: string) => {
    setLoading(true);
    try {
      const res = await usersApi.getList(kw || keyword || undefined);
      setUsers(res.data);
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    loadUsers('');
  }, []);

  const handleSearch = () => loadUsers(keyword);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  // 角色编辑
  const startEditRole = (user: UserItem) => {
    setEditingRoleId(user.id);
    setEditingRole(user.role.toUpperCase());
  };
  const cancelEditRole = () => {
    setEditingRoleId(null);
    setEditingRole('');
  };
  const confirmEditRole = async (userId: number) => {
    try {
      await usersApi.updateRole(userId, editingRole);
      showToast('角色修改成功');
      setEditingRoleId(null);
      loadUsers(keyword);
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '修改失败', 'error');
    }
  };

  // 充值确认
  const handleRecharge = async () => {
    if (!rechargeTarget) return;
    
    let tokens = 0;
    let amount = 0;
    
    if (rechargeMode === 'package') {
      if (!selectedPackage) {
        showToast('请选择充值套餐', 'error');
        return;
      }
      tokens = selectedPackage.tokens;
      amount = selectedPackage.price;
    } else {
      tokens = parseInt(customTokens) || 0;
      amount = parseFloat(customAmount) || 0;
      if (tokens <= 0) {
        showToast('请输入有效的Token数量', 'error');
        return;
      }
    }
    
    setRechargeLoading(true);
    try {
      await tokenApi.recharge({
        user_id: rechargeTarget.id,
        tokens_amount: tokens,
        payment_amount: amount,
        exchange_rate: `1元=${EXCHANGE_RATE}Token`,
        remark_type: rechargeMode === 'package' ? 'package' : 'custom',
      });
      showToast(`已为「${rechargeTarget.display_name}」充值 ${formatTokenCount(tokens)} Token`);
      setRechargeTarget(null);
      loadUsers(keyword);
    } catch (e: any) {
      showToast(e?.response?.data?.detail || '充值失败', 'error');
    } finally {
      setRechargeLoading(false);
    }
  };

  // 百分比颜色
  const pctColor = (pct: number) => {
    if (pct >= 60) return 'text-green-600';
    if (pct >= 20) return 'text-yellow-600';
    return 'text-red-600';
  };
  const pctBg = (pct: number) => {
    if (pct >= 60) return 'bg-green-500';
    if (pct >= 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex flex-col h-full bg-transparent">
      {/* 头部 */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">人员管理</h1>
          <p className="text-sm text-gray-500 mt-1">管理系统用户，查看Token使用情况</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 搜索框 */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜索用户名/显示名称..."
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-64 pl-10 pr-4 py-2.5 bg-gray-100 border-2 border-transparent rounded-xl text-sm text-gray-700 placeholder-gray-500 outline-none transition-all duration-200 hover:bg-white hover:shadow-md focus:bg-white focus:border-blue-400 focus:shadow-lg focus:shadow-blue-500/10"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium"
          >
            搜索
          </button>
          <button
            onClick={() => loadUsers(keyword)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:shadow-md transition-all duration-200 text-sm font-medium"
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </button>
        </div>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 mt-2 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 表格区域 */}
      <div className="flex-1 overflow-auto px-6 py-4 relative">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium w-40">用户</th>
                  <th className="text-center px-3 py-3 text-gray-600 font-medium w-16">项目数</th>
                  <th className="text-left px-4 py-3 text-gray-600 font-medium w-64">Token（总计 / 已用 / 剩余%）</th>
                  <th className="text-center px-3 py-3 text-gray-600 font-medium w-20">登录方式</th>
                  <th className="text-center px-3 py-3 text-gray-600 font-medium w-24">注册时间</th>
                  <th className="text-center px-3 py-3 text-gray-600 font-medium w-28">角色</th>
                  {isAdmin && (
                    <th className="text-center px-3 py-3 text-gray-600 font-medium w-20">操作</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 ? (
                <tr>
                  <td colSpan={isAdmin ? 7 : 6} className="py-16 text-center text-gray-400">
                    暂无数据
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    {/* 用户名 */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{user.display_name}</div>
                      <div className="text-xs text-gray-400">{user.username}</div>
                    </td>

                    {/* 项目数 */}
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-50 text-blue-700 rounded-full font-medium text-sm">
                        {user.project_count}
                      </span>
                    </td>

                    {/* Token */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-gray-500 text-xs">总</span>
                        <span className="text-gray-800 font-medium">{formatTokenCount(user.token_total)}</span>
                        <span className="text-gray-300 mx-0.5">/</span>
                        <span className="text-gray-500 text-xs">用</span>
                        <span className="text-gray-700">{formatTokenCount(user.token_used)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-32">
                          <div
                            className={`h-full rounded-full transition-all ${pctBg(user.token_remaining_pct)}`}
                            style={{ width: `${Math.min(100, user.token_remaining_pct)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${pctColor(user.token_remaining_pct)}`}>
                          剩余 {user.token_remaining_pct}%
                        </span>
                      </div>
                    </td>

                    {/* 登录方式 */}
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                        user.login_method === '飞书'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {user.login_method}
                      </span>
                    </td>

                    {/* 注册时间 */}
                    <td className="px-3 py-3 text-center text-gray-500 text-xs">
                      {formatDate(user.created_at)}
                    </td>

                    {/* 角色 */}
                    <td className="px-3 py-3 text-center">
                      {isSuper && editingRoleId === user.id ? (
                        <div className="flex items-center gap-1 justify-center">
                          <select
                            value={editingRole}
                            onChange={e => setEditingRole(e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-0.5 min-w-[7rem] focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            {ROLE_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          <button
                            onClick={() => confirmEditRole(user.id)}
                            className="p-0.5 text-green-600 hover:text-green-700"
                            title="确认"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditRole}
                            className="p-0.5 text-gray-400 hover:text-gray-600"
                            title="取消"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          {/* super 和 admin 都能看到角色 */}
                          {isAdmin && (
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              user.role === 'super'
                                ? 'bg-purple-50 text-purple-700'
                                : user.role === 'admin'
                                  ? 'bg-orange-50 text-orange-700'
                                  : 'bg-gray-100 text-gray-600'
                            }`}>
                              {ROLE_LABELS[user.role] || user.role}
                            </span>
                          )}
                          {/* 只有 super 可以编辑角色，且不能编辑自己 */}
                          {isSuper && user.id !== currentUser.id && (
                            <button
                              onClick={() => startEditRole(user)}
                              className="p-0.5 text-gray-300 hover:text-blue-500 transition-colors"
                              title="修改角色"
                            >
                              <PencilIcon className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>

                    {/* 操作 - 充值 */}
                    {isAdmin && (
                      <td className="px-3 py-3 text-center">
                        <button
                          onClick={() => openRechargeDialog(user)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                          title="充值"
                        >
                          <CurrencyDollarIcon className="w-3.5 h-3.5" />
                          充值
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
        {/* 统计信息 */}
        {!loading && users.length > 0 && (
          <div className="mt-3 text-xs text-gray-400 text-right">
            共 {users.length} 名用户
          </div>
        )}
      </div>

      {/* 充值弹窗 */}
      {rechargeTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-[480px] max-h-[90vh] overflow-auto p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Token 充值</h3>
            <p className="text-sm text-gray-500 mb-5">
              为 <span className="font-medium text-gray-800">{rechargeTarget.display_name}</span>
              <span className="text-gray-400">（{rechargeTarget.username}）</span> 充值
            </p>

            {/* 当前余额 */}
            <div className="bg-gray-50 rounded-xl p-3 mb-5 flex justify-between text-sm">
              <span className="text-gray-500">当前剩余</span>
              <span className="font-medium text-gray-800">
                {formatTokenCount(rechargeTarget.token_remaining)} Token
                <span className={`ml-1.5 text-xs ${pctColor(rechargeTarget.token_remaining_pct)}`}>
                  ({rechargeTarget.token_remaining_pct}%)
                </span>
              </span>
            </div>

            {/* 模式切换 */}
            <div className="flex gap-2 mb-5 p-1 bg-gray-100 rounded-lg">
              <button
                onClick={() => setRechargeMode('package')}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  rechargeMode === 'package'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                选择套餐
              </button>
              <button
                onClick={() => setRechargeMode('custom')}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  rechargeMode === 'custom'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                自定义充值
              </button>
            </div>

            {/* 套餐选择 */}
            {rechargeMode === 'package' && (
              <div className="space-y-2 mb-5">
                {rechargePackages.length > 0 ? (
                  rechargePackages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`p-3 border-2 rounded-xl cursor-pointer transition-all ${
                        selectedPackage?.id === pkg.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-400'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{pkg.name}</h4>
                          <p className="text-xs text-gray-500">{pkg.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">¥{pkg.price}</div>
                          <div className="text-xs text-gray-500">{formatTokenCount(pkg.tokens)} Token</div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">暂无可用套餐</p>
                )}
              </div>
            )}

            {/* 自定义充值 */}
            {rechargeMode === 'custom' && (
              <div className="space-y-4 mb-5">
                <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                  兑换比例：1元 = {formatTokenCount(EXCHANGE_RATE)} Token
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Token数量</label>
                  <input
                    type="number"
                    value={customTokens}
                    onChange={(e) => handleCustomTokensChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="请输入Token数量"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">支付金额（元）</label>
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => handleCustomAmountChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="请输入支付金额"
                    step="0.01"
                  />
                </div>
              </div>
            )}

            {/* 充值后预览 */}
            <div className="bg-blue-50 rounded-xl p-3 mb-5 text-sm flex justify-between items-center">
              <span className="text-blue-600">充值后剩余</span>
              <span className="font-semibold text-blue-800">
                {formatTokenCount(rechargeTarget.token_remaining + 
                  (rechargeMode === 'package' 
                    ? (selectedPackage?.tokens || 0) 
                    : (parseInt(customTokens) || 0)))} Token
              </span>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-3">
              <button
                onClick={() => setRechargeTarget(null)}
                disabled={rechargeLoading}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 transition-colors text-sm"
              >
                取消
              </button>
              <button
                onClick={handleRecharge}
                disabled={rechargeLoading}
                className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {rechargeLoading ? '充值中...' : '确认充值'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast 提示 */}
      <Toast
        show={toast.show}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default UserManagement;
